import { useContext } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import Autoplay from 'embla-carousel-autoplay'

import { CarouselButton, useCarouselButton } from '../CarouselButton'
import MicrocmsImage from '@atoms/MicrocmsImage'
import ExpandingImageLink from '@molecules/ExpandingImageLink'
import MaskedIcon from '@icons/Masked'
import { StoreContext } from '@models/Store'
import { ArtisanArticle } from '@src/pages/artisan'

import * as style from './style.module.sass'

export type HeroCarouselProps = {
  artisanArticles: ArtisanArticle[]
  carouselIds: string[]
}

const HeroCarousel = ({ artisanArticles, carouselIds }: HeroCarouselProps) => {
  const { entity } = useContext(StoreContext)
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, duration: 40 }, [
    Autoplay({
      delay: 6000,
      stopOnMouseEnter: true,
      stopOnInteraction: false,
    }),
  ])
  const { selectedIndex, scrollSnaps, scrollProgress, onCarouselButtonClick } =
    useCarouselButton(emblaApi)

  const carouselArtisanArticles = carouselIds
    .map(id => artisanArticles.find(({ article }) => article.blogId === id))
    .filter((article): article is ArtisanArticle => article !== undefined)
    .slice(0, 4)
  const publishDates = carouselArtisanArticles.map(
    ({ article }) => article.publishDate,
  )

  return (
    <div className={style.container}>
      <div className={style.outerCarouselContainer}>
        <div ref={emblaRef} className={style.carouselContainer}>
          <div className={style.carousel}>
            {carouselArtisanArticles.map(
              ({ article, location }, index: number) => {
                const isMasked =
                  entity?.user?.isSignIn !== true && article.isMembersOnly

                return (
                  <div
                    className={style.item}
                    key={index}
                    data-selected={index === selectedIndex}
                  >
                    <ExpandingImageLink to={article.path} isSlow>
                      <MicrocmsImage
                        image={article.thumbnail}
                        alt={article.title}
                        type="largeHero"
                        disableLazyLoad
                        disableAspectRatio
                      />
                      <div className={style.info}>
                        <div
                          className={style.infoContents}
                          data-selected={index === selectedIndex}
                        >
                          <div className={style.location}>{location}</div>
                          <span className={style.title}>
                            {article.title}
                            {isMasked && <MaskedIcon />}
                          </span>
                          <div className={style.insideButtonContainer}>
                            <HeroCarouselButton
                              scrollSnaps={scrollSnaps}
                              selectedIndex={selectedIndex}
                              scrollProgress={scrollProgress}
                              onCarouselButtonClick={onCarouselButtonClick}
                            />
                          </div>
                        </div>
                      </div>
                    </ExpandingImageLink>
                  </div>
                )
              },
            )}
          </div>
        </div>
        <div className={style.date}>{publishDates[selectedIndex]}</div>
        <div className={style.buttonContainer}>
          <HeroCarouselButton
            scrollSnaps={scrollSnaps}
            selectedIndex={selectedIndex}
            scrollProgress={scrollProgress}
            onCarouselButtonClick={onCarouselButtonClick}
          />
        </div>
      </div>
    </div>
  )
}

type HeroCarouselButtonProps = {
  scrollSnaps: number[]
  selectedIndex: number
  scrollProgress: number
  onCarouselButtonClick: (index: number) => void
}

const HeroCarouselButton: React.FC<HeroCarouselButtonProps> = ({
  scrollSnaps,
  selectedIndex,
  scrollProgress,
  onCarouselButtonClick,
}) => (
  <>
    {scrollSnaps.map((_, index) => {
      const denomitaor =
        index === scrollSnaps.length - 1
          ? 1 - scrollSnaps[index]
          : scrollSnaps[index + 1] - scrollSnaps[index]
      const numerator = scrollProgress - scrollSnaps[index] + denomitaor

      return (
        <CarouselButton
          key={index}
          onClick={() => onCarouselButtonClick(index)}
          className={style.button}
        >
          <div
            className={style.progressBar}
            style={{
              transform: `translateX(${
                (numerator >= 1 ? numerator - 1 : numerator / denomitaor) * 100
              }%)`,
            }}
          />
        </CarouselButton>
      )
    })}
    <span className={style.index}>
      {selectedIndex + 1}/{scrollSnaps.length}
    </span>
  </>
)

export default HeroCarousel
